import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  DrawerBody,
  GenericIconName,
  Icon,
  SPACING,
  Text,
  UnstyledButton,
  useInViewEffect,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import backgroundImage from '../assets/background-dark.png';
import {CoinsHistoryHeader} from './CoinsHistoryHeader';
import {CoinsHistoryPage} from './CoinsHistoryPage';
import {useCoinsHistoryOpenedState} from './hooks/useCoinsHistoryOpenedState';

const COINS_HISTORY_HEADER_HEIGHT = '60px';

export const CoinsHistory: React.FC<{
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
}> = ({ContentPopperLayoutComponent}) => {
  const {t} = useTranslation();

  const [hasScrolled, setHasScrolled] = useState(false);
  const {ref} = useInViewEffect<HTMLDivElement>(inView =>
    setHasScrolled(!inView),
  );

  const {cancel, isOpen} = useCoinsHistoryOpenedState();

  const [tokens, setTokens] = useHistoryPageState<string[]>([], {
    uniqueKey: 'coins-history',
  });

  /**
   * token have three states,
   * - undefined: initial state
   * - null: no more data
   * - string: continuation token
   */
  const [nextToken, setNextToken] = useHistoryPageState<
    string | undefined | null
  >(undefined, {
    uniqueKey: 'coins-history-next-token',
  });

  const loadNextPage = () => {
    if (nextToken) {
      setTokens([...tokens, nextToken]);
      setNextToken(undefined);
    }
  };

  const updateNextToken = (token: string | null) => {
    setNextToken(token);
  };

  return (
    <StyledContentPopper
      data-testid="coinsHistory"
      LayoutComponent={ContentPopperLayoutComponent}
      onClose={cancel}
      slideDirection="left"
      renderInPortal
      open={isOpen}
      preventSlideUpAnimation
      hideCloseButton
      bodyContent={
        <>
          <div ref={ref} />
          <Wrapper
            data-white-background={hasScrolled}
            data-testid="walletHeader"
          >
            <HeaderWrapper>
              <Text color={hasScrolled ? 'Black' : 'White'} size="px20">
                {t('Coins history')}
              </Text>
            </HeaderWrapper>
            <CloseButton
              data-testid="coinsHistoryCloseButton"
              onClick={cancel}
              aria-label={t('Close coins history')}
              $position="left"
              $color={hasScrolled ? COLORS.Black : COLORS.White}
            >
              <Icon
                asset={GenericIconName.ArrowLeft}
                size="S"
                fill={hasScrolled ? COLORS.Black : COLORS.White}
              />
            </CloseButton>
          </Wrapper>
          <ContentWrapper>
            <CoinsHistoryHeader />
            <BottomSheet>
              <CoinsHistoryPage
                loadNextPage={loadNextPage}
                updateNextToken={updateNextToken}
              />
              {tokens.map(token => (
                <CoinsHistoryPage
                  key={token}
                  continuationToken={token}
                  loadNextPage={loadNextPage}
                  updateNextToken={updateNextToken}
                />
              ))}
            </BottomSheet>
          </ContentWrapper>
        </>
      }
    />
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100% - ${COINS_HISTORY_HEADER_HEIGHT});
  width: 100%;
  overflow-y: auto;
`;

const BottomSheet = styled.div`
  background-color: ${COLORS.White};
  flex: 1;
  border-top-right-radius: 1rem;
  border-top-left-radius: 1rem;
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  width: 100%;

  z-index: 3;

  transition-property: background-color, box-shadow;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  &[data-white-background='true'] {
    background-color: ${COLORS.White};
    box-shadow: 0px 2px 8px -3px ${COLORS.Black};
  }
`;

const HeaderWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  flex: 1;
  padding: 1.25rem 0;
  height: ${COINS_HISTORY_HEADER_HEIGHT};
`;

const CloseButton = styled(UnstyledButton)<{
  $color: string;
  $position: 'left' | 'right';
}>`
  position: absolute;
  left: ${props => (props.$position === 'left' ? '16px' : 'unset')};
  right: ${props => (props.$position === 'right' ? '16px' : 'unset')};
  cursor: pointer;
`;

const StyledContentPopper = styled(ContentPopper)<{
  $isHighTierCouponsActive?: boolean;
}>`
  ${DrawerBody} {
    padding: 0;
    background-color: transparent;
  }

  overflow: auto;

  z-index: ${Z_INDEXES.COINS_HISTORY};

  &::before {
    position: fixed;
    z-index: -1;
    inset: 0;
    content: '';
    background-size: cover;
    background-image: url(${backgroundImage});
    max-height: 250px;
  }
`;
